import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Heading } from "../components/ui/Heading";
import { Text } from "../components/ui/Text";
import { Button } from "../components/ui/Button";
import { QuantitySelector } from "../components/ui/QuantitySelector";
import { Price } from "../components/ui/Price";
import { Image } from "../components/ui/Image";
import { Spinner } from "../components/ui/Spinner";
import { PrintDesigner } from "../components/productCustomization/PrintDesigner";
import { ThreeDPrintDesigner } from "../components/productCustomization/ThreeDPrintDesigner";
import { DesignSelectionModal } from "../components/productCustomization/DesignSelectionModal";
import { useApi } from "../context/ApiContext";
import { productCustomizationApi } from "../services/productCustomizationApi";
import {
  getResizedImageUrl,
  getThumbnailUrl,
  getImageUrl,
  ImagePresets,
} from "../utils/imageUtils";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { PageZone } from "../components/pageBuilder/PageZone";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import {
  calculateAvailableInventory,
  isOutOfStock,
  getInventoryDisplay,
  findFirstAvailableVariant,
  calculateProductDetailPricing,
} from "../utils/inventoryUtils";
import { Badge } from "../components/ui/Badge";
import { useTranslatedValue } from "../hooks/useTranslatedValue";
import ImageZoomPan from "../components/ImageZoomPan";
import { useCartAnimationContext } from "../context/CartAnimationContext";
import { useStore } from "../context/StoreContext";
import { ShareButton } from "../components/ShareButton";
import { getAbsoluteUrl, truncateDescription } from "../utils/metaUtils";
import { TieredDiscountDisplay } from "../components/product/TieredDiscountDisplay";

export const ProductDetail: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { api } = useApi();
  const { currency, location } = useCurrency();
  const { addToCart, addMultipleDesignsToCart } = useCart();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const { triggerAnimation } = useCartAnimationContext();
  const { store, isPluginEnabled } = useStore();
  const addToCartButtonRef = useRef<HTMLButtonElement>(null);

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantOptions, setSelectedVariantOptions] = useState<
    Record<string, string>
  >({});
  const [quantity, setQuantity] = useState(1);

  // Calculate pricing with discounts
  const pricing = useMemo(() => {
    if (!product) return { price: 0, originalPrice: 0 };

    const selectedOptions = Object.entries(selectedVariantOptions).map(
      ([optionName, optionValue]) => ({
        optionName,
        optionValue,
      }),
    );

    return calculateProductDetailPricing(
      product,
      selectedOptions,
      location?.locationId,
      quantity,
      isPluginEnabled,
    );
  }, [
    product,
    selectedVariantOptions,
    location?.locationId,
    quantity,
    isPluginEnabled,
  ]);

  const { price: discountedPrice, originalPrice } = pricing;
  // Check if customization is enabled (both product flag and plugin)
  const isCustomizationEnabled =
    product?.isCustomizable && isPluginEnabled("product-customization");

  useEffect(() => {
    if (product) {
      // Update quantity if it exceeds available inventory for selected combination
      const available = calculateAvailableInventory(
        product,
        selectedVariantOptions,
      );
      setQuantity((prevQuantity) => {
        if (available > 0 && prevQuantity > available) {
          return available;
        }
        return prevQuantity;
      });
    }
  }, [product, selectedVariantOptions]);

  useEffect(() => {
    if (product?.isDigital && quantity !== 1) {
      setQuantity(1);
    }
  }, [product?.isDigital, quantity]);

  const [showCustomizer, setShowCustomizer] = useState(false);
  const [customizationId, setCustomizationId] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isZoomPanOpen, setIsZoomPanOpen] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState<any[]>([]);
  const [showDesignSelectionModal, setShowDesignSelectionModal] =
    useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Check if the parameter is a MongoDB ObjectId (24 hex characters)
  const isObjectId = useMemo(() => {
    if (!handle) return false;
    // MongoDB ObjectId is 24 hex characters
    return /^[0-9a-fA-F]{24}$/.test(handle);
  }, [handle]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      // Use ID endpoint if it's an ObjectId, otherwise use handle endpoint
      const endpoint = isObjectId 
        ? `/products/${handle}` 
        : `/products/handle/${handle}`;
      const response = await api.get(endpoint);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
      navigate("/products");
    } finally {
      setLoading(false);
    }
  }, [api, handle, navigate, isObjectId]);

  useEffect(() => {
    if (handle) {
      fetchProduct();
    }
  }, [handle, currency, fetchProduct]);

  // Fetch saved designs when product is loaded
  useEffect(() => {
    if (product?._id && isCustomizationEnabled) {
      fetchSavedDesigns();
    }
  }, [product?._id, isCustomizationEnabled]);

  const fetchSavedDesigns = useCallback(async () => {
    if (!product?._id) return;

    try {
      const designs = await productCustomizationApi.getCustomizations({
        productId: product._id,
        customizationType: product.customizationType,
      });
      // Filter to only show draft designs
      const drafts = designs.filter((d: any) => d.isDraft);
      setSavedDesigns(drafts);
    } catch (error) {
      console.error("Error fetching saved designs:", error);
    }
  }, [product?._id, product?.customizationType]);

  // Preselect first available variant combination when product loads
  useEffect(() => {
    if (
      product &&
      product.variantOptions &&
      product.variantOptions.length > 0
    ) {
      // Only preselect if no variant options are currently selected
      if (Object.keys(selectedVariantOptions).length === 0) {
        const locationId = location?.locationId;
        // Find the first available (non out of stock) variant combination
        const firstAvailableCombination = findFirstAvailableVariant(
          product,
          locationId,
        );

        // If no available variant found, fall back to first active value from each option
        const combinationToUse =
          firstAvailableCombination ||
          (() => {
            const firstCombination: Record<string, string> = {};
            for (const option of product.variantOptions) {
              const firstActiveValue = option.values.find(
                (v: any) => v.isActive,
              );
              if (firstActiveValue) {
                firstCombination[option.name] = firstActiveValue.value;
              }
            }
            return Object.keys(firstCombination).length > 0
              ? firstCombination
              : null;
          })();

        // Only set if we have a valid combination
        if (combinationToUse && Object.keys(combinationToUse).length > 0) {
          setSelectedVariantOptions(combinationToUse);

          // Set image index to match the combination's image or primary image
          const combinationString = Object.entries(combinationToUse)
            .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
            .sort()
            .join("|");

          // Check if there's a variant combination with an image
          if (
            product.variantCombinations &&
            product.variantCombinations.length > 0
          ) {
            const variantCombo = product.variantCombinations.find(
              (vc: any) => vc.combination === combinationString,
            );
            if (
              variantCombo &&
              variantCombo.imageIndex !== undefined &&
              variantCombo.imageIndex >= 0
            ) {
              setSelectedImageIndex(variantCombo.imageIndex);
              return;
            }
          }

          // Fallback to primary image or first image
          const primaryIndex = product.images?.findIndex(
            (img: any) => img.isPrimary,
          );
          if (primaryIndex !== -1) {
            setSelectedImageIndex(primaryIndex);
          } else if (product.images && product.images.length > 0) {
            setSelectedImageIndex(0);
          }
        }
      }
    }
  }, [product, selectedVariantOptions, location]);

  useEffect(() => {
    if (product) {
      // Update quantity if it exceeds available inventory for selected combination
      const available = calculateAvailableInventory(
        product,
        selectedVariantOptions,
      );
      setQuantity((prevQuantity) => {
        if (available > 0 && prevQuantity > available) {
          return available;
        }
        return prevQuantity;
      });
    }
  }, [product, selectedVariantOptions]);

  const handleVariantOptionChange = (optionName: string, value: string) => {
    setSelectedVariantOptions((prev) => ({
      ...prev,
      [optionName]: value,
    }));
  };

  // Build variant combination string from selected options
  const buildVariantCombination = (options: Record<string, string>): string => {
    return Object.entries(options)
      .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
      .sort()
      .join("|");
  };

  // Find image index that matches the current variant combination
  const findMatchingImageIndex = useMemo(() => {
    if (!product?.images || product.images.length === 0) {
      return 0;
    }

    const combination = buildVariantCombination(selectedVariantOptions);

    // If we have a combination, check variantCombinations array first (new structure)
    if (combination && product.variantCombinations) {
      const variantCombo = product.variantCombinations.find(
        (vc: any) => vc.combination === combination,
      );
      if (
        variantCombo &&
        variantCombo.imageIndex !== undefined &&
        variantCombo.imageIndex >= 0
      ) {
        return variantCombo.imageIndex;
      }
    }

    // Fallback to old structure: check images for variantCombinations
    if (combination) {
      const matchingIndex = product.images.findIndex(
        (img: any) =>
          img.variantCombinations &&
          img.variantCombinations.includes(combination),
      );
      if (matchingIndex !== -1) {
        return matchingIndex;
      }
    }

    // Check for variant option value images
    if (Object.keys(selectedVariantOptions).length > 0) {
      for (const option of product.variantOptions || []) {
        const selectedValue = selectedVariantOptions[option.name];
        if (selectedValue) {
          const optionValue = option.values.find(
            (v: any) => v.value === selectedValue && v.isActive,
          );
          if (optionValue?.image) {
            // Find image index by URL
            const imageIndex = product.images.findIndex(
              (img: any) =>
                img.url === optionValue.image ||
                img.originalUrl === optionValue.image,
            );
            if (imageIndex !== -1) {
              return imageIndex;
            }
          }
        }
      }
    }

    // Fallback to primary image or first image
    const primaryIndex = product.images.findIndex((img: any) => img.isPrimary);
    return primaryIndex !== -1 ? primaryIndex : 0;
  }, [product, selectedVariantOptions]);

  // Handle image click - select all variant option values for that image
  const handleImageClick = (imageIndex: number) => {
    if (!product || !product.variantOptions) return;

    const newSelections: Record<string, string> = {};

    // First, check variantCombinations array (new structure)
    if (product.variantCombinations && product.variantCombinations.length > 0) {
      // Find all variant combinations that use this image
      const matchingCombos = product.variantCombinations.filter(
        (vc: any) => vc.imageIndex === imageIndex,
      );

      if (matchingCombos.length > 0) {
        // Prefer complete combinations (that include all variant options) over incomplete ones
        const allOptionNames = new Set(
          product.variantOptions
            .map((opt: any) => opt.name)
            .filter(
              (name: any): name is string =>
                typeof name === "string" && name.length > 0,
            ),
        );

        // Find complete combinations first
        const completeCombo = matchingCombos.find((combo: any) => {
          const pairs = combo.combination.split("|");
          const combinationOptionNames = new Set(
            pairs.map((p: string) => p.split(":")[0].trim()),
          );
          return (
            combinationOptionNames.size === allOptionNames.size &&
            Array.from(allOptionNames).every((name) =>
              combinationOptionNames.has(name),
            )
          );
        });

        // Use complete combination if found, otherwise use first matching
        const matchingCombo = completeCombo || matchingCombos[0];
        const combination = matchingCombo.combination;

        // Parse the combination string - format: "Size:Small|Color:Red"
        const pairs = combination.split("|");
        pairs.forEach((pair: string) => {
          const trimmedPair = pair.trim();
          const colonIndex = trimmedPair.indexOf(":");
          if (colonIndex > 0) {
            const optionName = trimmedPair.substring(0, colonIndex).trim();
            const value = trimmedPair.substring(colonIndex + 1).trim();

            // Find matching option by name (case-insensitive, handle whitespace)
            const option = product.variantOptions.find(
              (opt: any) => opt.name && opt.name.trim() === optionName,
            );

            if (option) {
              // Find matching value (case-insensitive, handle whitespace)
              const optionValue = option.values.find(
                (v: any) => v.value && v.value.trim() === value && v.isActive,
              );

              if (optionValue) {
                // Use the actual option name and value from the product (not from combination string)
                newSelections[option.name] = optionValue.value;
              }
            }
          }
        });
      }
    }

    // Fallback: Check images array for variantCombinations (old structure)
    if (
      Object.keys(newSelections).length === 0 &&
      product.images &&
      product.images[imageIndex]
    ) {
      const image = product.images[imageIndex];
      if (
        image.variantCombinations &&
        Array.isArray(image.variantCombinations) &&
        image.variantCombinations.length > 0
      ) {
        // Use the first variant combination from the image
        const combination = image.variantCombinations[0];
        const pairs = combination.split("|");
        pairs.forEach((pair: string) => {
          const trimmedPair = pair.trim();
          const colonIndex = trimmedPair.indexOf(":");
          if (colonIndex > 0) {
            const optionName = trimmedPair.substring(0, colonIndex).trim();
            const value = trimmedPair.substring(colonIndex + 1).trim();

            // Find matching option by name (case-insensitive, handle whitespace)
            const option = product.variantOptions.find(
              (opt: any) => opt.name && opt.name.trim() === optionName,
            );

            if (option) {
              // Find matching value (case-insensitive, handle whitespace)
              const optionValue = option.values.find(
                (v: any) => v.value && v.value.trim() === value && v.isActive,
              );

              if (optionValue) {
                // Use the actual option name and value from the product (not from combination string)
                newSelections[option.name] = optionValue.value;
              }
            }
          }
        });
      }
    }

    // Only update if we found matching combinations
    if (Object.keys(newSelections).length > 0) {
      setSelectedVariantOptions(newSelections);
    }
  };

  // Update selected image index when variant selection changes
  useEffect(() => {
    if (product?.images && product.images.length > 0) {
      const matchingIndex = findMatchingImageIndex;
      setSelectedImageIndex(matchingIndex);
    }
  }, [findMatchingImageIndex, product]);

  // Check if all variant options are selected (if product has variants)
  const allVariantsSelected = (): boolean => {
    if (!product?.variantOptions || product.variantOptions.length === 0) {
      return true; // No variants, so selection is valid
    }

    // If product has variants, all options must be selected
    return product.variantOptions.every(
      (option: any) => selectedVariantOptions[option.name],
    );
  };

  // Check if all required variant options are selected (for backward compatibility)
  const allRequiredSelected = (): boolean => {
    if (!product?.variantOptions || product.variantOptions.length === 0) {
      return true;
    }
    return product.variantOptions.every(
      (option: any) => !option.required || selectedVariantOptions[option.name],
    );
  };

  const handleAddToCart = async () => {
    if (!product) return;

    // Validate variant options - if product has variants, all must be selected
    if (product.variantOptions && product.variantOptions.length > 0) {
      if (!allVariantsSelected()) {
        alert(
          t("productDetail.selectAllVariants") ||
            "Please select all variant options",
        );
        return;
      }
    } else if (!allRequiredSelected()) {
      // Fallback for products without variants but with required fields
      alert(
        t("productDetail.selectRequiredVariants") ||
          "Please select all required variant options",
      );
      return;
    }

    // For customizable products, show design selection modal
    if (isCustomizationEnabled && savedDesigns.length > 0) {
      setShowDesignSelectionModal(true);
      return;
    }

    // For non-customizable products or products without saved designs, add directly
    try {
      setIsAddingToCart(true);
      // Build selected variant options array
      const selectedOptions = Object.entries(selectedVariantOptions).map(
        ([optionName, optionValue]) => ({
          optionName,
          optionValue,
        }),
      );

      await addToCart(
        product._id,
        product.isDigital ? 1 : quantity, // Always use quantity 1 for digital products
        customizationId || undefined,
        selectedOptions.length > 0 ? selectedOptions : undefined,
      );
      // Trigger animation after adding to cart
      if (addToCartButtonRef.current) {
        triggerAnimation(addToCartButtonRef.current);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to add item to cart";
      alert(errorMessage); // TODO: Replace with proper toast/notification system
      console.error("Error adding to cart:", error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleDesignSelectionConfirm = async (
    selections: Array<{ customizationId: string; quantity: number }>,
  ) => {
    if (!product || selections.length === 0) return;

    try {
      setIsAddingToCart(true);
      // Build selected variant options array
      const selectedOptions = Object.entries(selectedVariantOptions).map(
        ([optionName, optionValue]) => ({
          optionName,
          optionValue,
        }),
      );

      // Ensure quantity is always 1 for digital products
      const normalizedSelections = product.isDigital
        ? selections.map((s) => ({ ...s, quantity: 1 }))
        : selections;

      await addMultipleDesignsToCart(
        product._id,
        normalizedSelections,
        selectedOptions.length > 0 ? selectedOptions : undefined,
      );
      // Trigger animation after adding to cart
      if (addToCartButtonRef.current) {
        triggerAnimation(addToCartButtonRef.current);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to add designs to cart";
      alert(errorMessage);
      console.error("Error adding designs to cart:", error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleSaveCustomization2D = async (customizationId: string) => {
    if (!product) return;

    try {
      setCustomizationId(customizationId);
      setShowCustomizer(false);
      // Refresh saved designs to show the new one
      await fetchSavedDesigns();
    } catch (error) {
      console.error("Error saving customization:", error);
    }
  };

  // Get translated values (must be called before any early returns)
  // Hooks must be called unconditionally, so we pass a default object if product is null
  const productTitle = useTranslatedValue(product || { title: "" }, "title");
  const productDescription = useTranslatedValue(
    product || { description: "" },
    "description",
  );

  // Helper function to get translated variant option name
  const getTranslatedOptionName = (option: any): string => {
    const currentLang = (i18n.language || "en").split("-")[0] as
      | "en"
      | "de"
      | "ar";
    if (currentLang === "en" || !option.translations) {
      return option.name;
    }

    // Handle both Map and plain object formats
    let translation: string | undefined;
    if (option.translations instanceof Map) {
      translation = option.translations.get(currentLang);
    } else if (typeof option.translations === "object") {
      translation = option.translations[currentLang];
    }

    return translation || option.name;
  };

  // Helper function to get translated variant option value
  const getTranslatedOptionValue = (value: any): string => {
    const currentLang = (i18n.language || "en").split("-")[0] as
      | "en"
      | "de"
      | "ar";
    if (currentLang === "en" || !value.translations) {
      return value.value;
    }

    // Handle both Map and plain object formats
    let translation: string | undefined;
    if (value.translations instanceof Map) {
      translation = value.translations.get(currentLang);
    } else if (typeof value.translations === "object") {
      translation = value.translations[currentLang];
    }

    return translation || value.value;
  };

  // Get variant combination description if available
  const variantCombinationDescription = useMemo(() => {
    if (
      !product ||
      !selectedVariantOptions ||
      Object.keys(selectedVariantOptions).length === 0
    ) {
      return null;
    }

    const combination = Object.entries(selectedVariantOptions)
      .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
      .sort()
      .join("|");

    if (!combination || !product.variantCombinations) {
      return null;
    }

    const variantCombo = product.variantCombinations.find(
      (vc: any) => vc.combination === combination,
    );

    if (!variantCombo) {
      return null;
    }

    const currentLang =
      (i18n.language.split("-")[0] as "en" | "de" | "ar") || "en";

    // Priority: variant combination description translation > variant combination description
    // Handle both Map and plain object formats for translations
    // Check translations for all languages (including English) - descriptions may be stored in translations
    if (variantCombo.translations) {
      let translation: string | undefined;
      if (variantCombo.translations instanceof Map) {
        translation = variantCombo.translations.get(currentLang);
      } else if (typeof variantCombo.translations === "object") {
        translation = variantCombo.translations[currentLang];
      }
      if (translation) {
        return translation;
      }
    }

    return variantCombo.description || null;
  }, [product, selectedVariantOptions, i18n.language]);

  // Get the description to display (priority: variant combination > product description)
  const displayDescription =
    variantCombinationDescription || productDescription;

  // Filter digital files by variant selection and user's country
  // Priority: variant-level files > product-level files
  /* const availableDigitalFiles = useMemo(() => {
  if (!product?.isDigital) {
   return [];
  } 

  const userCountryCode = location?.countryCode?.toUpperCase();

  // Build variant combination string from selected options
  let variantCombination: string | undefined;
  if (selectedVariantOptions && Object.keys(selectedVariantOptions).length > 0) {
   variantCombination = Object.entries(selectedVariantOptions)
    .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
    .sort()
    .join("|");
  }

  // Helper function to filter files by country
  const filterByCountry = (files: any[]): any[] => {
   if (!userCountryCode) {
    // If no country detected, show files with no country mapping
    return files.filter((file: any) => 
     !file.countryCodes || file.countryCodes.length === 0
    );
   }

   return files.filter((file: any) => {
    // If file has no countryCodes or empty array, it's available for all countries
    if (!file.countryCodes || file.countryCodes.length === 0) {
     return true;
    }
    // Otherwise, check if user's country is in the file's countryCodes
    return file.countryCodes.includes(userCountryCode);
   });
  };

  // Priority 1: Check variant-level files (if variant combination exists)
  let variantFiles: any[] = [];
  if (variantCombination && product.variantCombinations) {
   const variantCombo = product.variantCombinations.find(
    (vc: any) => vc.combination === variantCombination
   );
   if (variantCombo && variantCombo.digitalFiles && variantCombo.digitalFiles.length > 0) {
    variantFiles = filterByCountry(variantCombo.digitalFiles);
   }
  }

  // Priority 2: Check product-level files (if no variant files found)
  const productLevelFiles = product.digitalFiles || [];
  const filteredProductFiles = variantFiles.length > 0 ? [] : filterByCountry(productLevelFiles);

  // Return variant files if available, otherwise product-level files
  return variantFiles.length > 0 ? variantFiles : filteredProductFiles;
 }, [product?.isDigital, product?.digitalFiles, product?.variantCombinations, selectedVariantOptions, location?.countryCode]);
*/
  // Check if description needs "read more" (strip HTML to check text length)
  const needsReadMore = useMemo(() => {
    if (!displayDescription) return false;
    const textOnly = displayDescription.replace(/<[^>]*>/g, "").trim();
    return textOnly.length > 200;
  }, [displayDescription]);

  // Helper function to check if URL is a video
  const isVideoUrl = (url: string): boolean => {
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some((ext) => lowerUrl.includes(ext));
  };

  // Get all product images and videos with proper URLs
  const productImages = useMemo(() => {
    if (!product?.images || product.images.length === 0) {
      return [];
    }

    const currentLang =
      (i18n.language.split("-")[0] as "en" | "de" | "ar") || "en";

    return product.images.map((img: any) => {
      const translatedUrl = img.translations?.[currentLang];
      const imageUrl = translatedUrl || img.url;
      const isVideo = img.type === "video" || isVideoUrl(imageUrl);

      return {
        ...img,
        type: isVideo ? "video" : "image",
        displayUrl: imageUrl,
        thumbnailUrl: isVideo
          ? imageUrl
          : getThumbnailUrl(imageUrl, storeLogoPlaceholder),
        detailUrl: isVideo
          ? imageUrl
          : getResizedImageUrl(
              imageUrl,
              ImagePresets.productDetail,
              storeLogoPlaceholder,
            ),
        fullUrl: imageUrl,
      };
    });
  }, [product, i18n.language, storeLogoPlaceholder]);

  // Get current image or video URL based on selected index
  const currentImage = useMemo(() => {
    if (productImages.length === 0) {
      return {
        detailUrl: storeLogoPlaceholder,
        fullUrl: storeLogoPlaceholder,
        alt: productTitle,
        type: "image" as "image" | "video",
      };
    }

    const image = productImages[selectedImageIndex] || productImages[0];
    return {
      detailUrl: image.detailUrl,
      fullUrl: image.fullUrl,
      alt: image.alt || productTitle,
      type: image.type || "image",
    };
  }, [productImages, selectedImageIndex, productTitle, storeLogoPlaceholder]);

  // Get background images from product assets library only (must be before early returns)
  const backgroundImages = useMemo(() => {
    // Only use backgrounds from the assets library (selected backgrounds for the product)
    if (product?.availableAssetIds && product.availableAssetIds.length > 0) {
      return product.availableAssetIds
        .filter((asset: any) => asset && asset.type === "background")
        .map((asset: any) => asset.url);
    }
    return [];
  }, [product]);

  const availableShapes = useMemo(() => {
    if (product?.availableAssetIds && product.availableAssetIds.length > 0) {
      return product.availableAssetIds
        .filter((asset: any) => asset && asset.type === "shape")
        .map((asset: any) => ({
          id: asset._id,
          name: asset.name,
          url: asset.url,
          category: asset.category,
        }));
    }
    return [];
  }, [product]);

  if (loading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12">
        <Heading size="xl">Product not found</Heading>
      </div>
    );
  }

  // Calculate inventory (with location-specific inventory if available)
  const locationId = location?.locationId;
  const availableInventory = calculateAvailableInventory(
    product,
    selectedVariantOptions,
    locationId,
  );
  const outOfStock = isOutOfStock(product, selectedVariantOptions, locationId);
  const inventoryDisplay = getInventoryDisplay(
    product,
    selectedVariantOptions,
    locationId,
  );

  // Prepare meta tags data
  const productUrl = getAbsoluteUrl(`/products/${handle}`);
  const metaTitle = productTitle;
  const metaDescription = truncateDescription(
    displayDescription || productDescription || "",
    155,
  );
  // Get primary image URL - use getImageUrl to ensure absolute URL
  const primaryImageUrl =
    product?.images && product.images.length > 0
      ? product.images.find((img: any) => img.isPrimary)?.url ||
        product.images[0]?.url
      : null;
  const metaImage = primaryImageUrl
    ? getImageUrl(primaryImageUrl, storeLogoPlaceholder)
    : storeLogoPlaceholder;
  const siteName = store?.name || "Ecommerce Store";

  return (
    <div className="w-full">
      <Helmet>
        <title>
          {metaTitle} | {siteName}
        </title>
        <meta name="description" content={metaDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={productUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:site_name" content={siteName} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={productUrl} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>
      {showCustomizer && isCustomizationEnabled ? (
        product.customizationType === "2d-print" ? (
          <PrintDesigner
            productId={product._id}
            baseImage={currentImage.detailUrl}
            canvasSize={product.canvasSize || "mug"}
            onSave={handleSaveCustomization2D}
            onCancel={() => {
              setShowCustomizer(false);
              setCustomizationId(null);
            }}
            backgroundImages={backgroundImages}
            availableShapes={availableShapes}
            allowedDesignerFonts={product.allowedDesignerFonts}
            draftCustomizationId={customizationId || undefined}
            onDraftSaved={() => {
              fetchSavedDesigns();
            }}
          />
        ) : product.customizationType === "3d-print" ? (
          <ThreeDPrintDesigner
            productId={product._id}
            onSave={(customizationId) => {
              setCustomizationId(customizationId);
              setShowCustomizer(false);
              fetchSavedDesigns();
            }}
            onCancel={() => setShowCustomizer(false)}
            draftCustomizationId={customizationId || undefined}
            onDraftSaved={() => {
              fetchSavedDesigns();
            }}
          />
        ) : null
      ) : (
        <div className="w-full bg-white py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm mb-6">
              <Link
                to="/"
                className="text-store-primary hover:text-store-secondary transition-colors"
              >
                {t("breadcrumb.home")}
              </Link>
              <span className="text-store-accent">/</span>
              <Link
                to="/products"
                className="text-store-primary hover:text-store-secondary transition-colors"
              >
                {t("breadcrumb.products")}
              </Link>
              <span className="text-store-accent">/</span>
              <span className="text-store-primary-light">{productTitle}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {/* Product Image Gallery */}
              <div className="w-full order-1 md:order-1">
                {/* Main Image or Video */}
                <div
                  className="relative aspect-square bg-store-background overflow-hidden mb-4 cursor-pointer group"
                  onClick={() => {
                    setIsZoomPanOpen(true);
                  }}
                >
                  {currentImage.type === "video" ? (
                    <video
                      src={currentImage.detailUrl}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                    >
                      {t("productDetail.videoNotSupported") ||
                        "Your browser does not support video playback"}
                    </video>
                  ) : (
                    <>
                      <Image
                        src={currentImage.detailUrl}
                        alt={currentImage.alt}
                        aspectRatio="square"
                        className="w-full h-full object-contain transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center pointer-events-none">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white bg-black/50 px-4 py-2 rounded pointer-events-auto">
                          {t("productDetail.clickToZoom") || "Click to zoom"}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Image/Video Thumbnails */}
                {productImages.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {productImages.map((img: any, index: number) => {
                      const isVideo =
                        img.type === "video" || isVideoUrl(img.displayUrl);
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImageIndex(index);
                            handleImageClick(index);
                          }}
                          className={`relative flex-shrink-0 w-20 h-20 border-2 transition-all ${
                            selectedImageIndex === index
                              ? "border-store-secondary ring-2 ring-store-secondary/50"
                              : "border-gray-300 hover:border-store-accent-light"
                          }`}
                        >
                          {isVideo ? (
                            <>
                              <video
                                src={img.thumbnailUrl}
                                className="w-full h-full object-cover pointer-events-none"
                                preload="metadata"
                                muted
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                                <svg
                                  className="w-6 h-6 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <img
                              src={img.thumbnailUrl}
                              alt={img.alt || `${productTitle} - ${index + 1}`}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {outOfStock && !product.isDigital && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge variant="error" size="lg">
                      {t("productDetail.outOfStock")}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Product Info - Sticky column */}
              <div className="order-2 md:order-2">
                <div className="md:sticky md:top-4 space-y-6 flex flex-col">
                  {/* Title */}
                  <div>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <Heading size="2xl" className="text-store-primary flex-1">
                        {productTitle}
                      </Heading>
                      <ShareButton
                        url={productUrl}
                        title={metaTitle}
                        description={metaDescription}
                        className="flex-shrink-0"
                      />
                    </div>
                  </div>

                  {/* Digital Files List */}
                  {/* {product.isDigital && availableDigitalFiles.length > 0 && (
          <div className="space-y-2">
           <Text weight="semibold" className="text-sm text-store-primary">
            {t("productDetail.digitalFiles") || "Digital Files Included"}:
           </Text>
           <ul className="list-disc list-inside space-y-1 text-sm text-store-primary-light">
            {availableDigitalFiles.map((file: any, index: number) => (
             <li key={file.key || index}>
              {file.filename}
             </li>
            ))}
           </ul>
          </div>
         )} */}

                  {/* Variant Options - Scrollable if needed */}
                  {product.variantOptions &&
                    product.variantOptions.length > 0 && (
                      <div className="max-h-[200px] overflow-y-auto space-y-3">
                        {product.variantOptions.map((option: any) => (
                          <div key={option.name}>
                            <Text
                              weight="semibold"
                              className="mb-1.5 text-sm text-store-primary"
                            >
                              {getTranslatedOptionName(option)}
                              {option.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                              :
                            </Text>
                            <div className="flex flex-wrap gap-2">
                              {option.values
                                .filter((v: any) => v.isActive)
                                .map((value: any) => (
                                  <button
                                    key={value.value}
                                    onClick={() =>
                                      handleVariantOptionChange(
                                        option.name,
                                        value.value,
                                      )
                                    }
                                    className={`px-3 py-1.5 border transition-all text-xs ${
                                      selectedVariantOptions[option.name] ===
                                      value.value
                                        ? "bg-store-primary text-white border-store-primary"
                                        : "bg-white text-store-primary border-store-accent-light hover:border-store-secondary"
                                    }`}
                                  >
                                    {getTranslatedOptionValue(value)}
                                    {value.addOnPrice !== 0 && (
                                      <span className="ml-1 text-xs">
                                        ({value.addOnPrice > 0 ? "+" : ""}
                                        {currency} {value.addOnPrice.toFixed(2)}
                                        )
                                      </span>
                                    )}
                                  </button>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Quantity and Price - Combined - Hidden for customizable products with saved designs and digital products */}
                  {!(isCustomizationEnabled && savedDesigns.length > 0) &&
                    !product.isDigital && (
                      <div className="h-[80px]">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Text
                              weight="semibold"
                              className="text-sm text-store-primary"
                            >
                              {t("productDetail.quantity")}:
                            </Text>
                            {outOfStock && !product.isDigital ? (
                              <Text
                                size="xs"
                                color="error"
                                className="text-red-600 font-semibold"
                              >
                                {t("productDetail.outOfStock")}
                              </Text>
                            ) : inventoryDisplay !== null &&
                              !product.isDigital ? (
                              <Text
                                size="xs"
                                color="muted"
                                className="text-gray-600"
                              >
                                {t("productDetail.available")}:{" "}
                                {inventoryDisplay}
                              </Text>
                            ) : null}
                          </div>
                          <div className="text-right">
                            <Price
                              amount={discountedPrice * quantity}
                              originalAmount={originalPrice * quantity}
                              currency={currency}
                              size="lg"
                              className="text-store-accent font-bold text-xl"
                            />
                            <div className="h-[16px] mt-0.5">
                              {quantity > 1 ? (
                                <Text size="xs" color="muted">
                                  {currency} {discountedPrice.toFixed(2)} each
                                </Text>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <QuantitySelector
                          value={quantity}
                          onChange={setQuantity}
                          min={1}
                          max={
                            availableInventory > 0 ? availableInventory : 999
                          }
                          disabled={outOfStock && !product.isDigital}
                        />
                      </div>
                    )}

                  {/* Tiered Discounts Display */}
                  {!product.isDigital && (
                    <TieredDiscountDisplay
                      product={product}
                      selectedVariantOptions={selectedVariantOptions}
                      locationId={location?.locationId}
                      currentQuantity={quantity}
                      currency={currency}
                      isPluginEnabled={isPluginEnabled}
                      onQuantityChange={setQuantity}
                    />
                  )}

                  {/* Price Display for Digital Products (no quantity selector) */}
                  {product.isDigital &&
                    !(isCustomizationEnabled && savedDesigns.length > 0) && (
                      <div className="h-[40px] flex items-center justify-end">
                        <Price
                          amount={discountedPrice}
                          originalAmount={
                            originalPrice !== discountedPrice
                              ? originalPrice
                              : undefined
                          }
                          currency={currency}
                          size="lg"
                          className="text-store-accent font-bold text-xl"
                        />
                      </div>
                    )}

                  {/* Price Display for Customizable Products */}
                  {isCustomizationEnabled && savedDesigns.length > 0 && (
                    <div className="h-[40px] flex items-center justify-end">
                      <Price
                        amount={discountedPrice}
                        originalAmount={originalPrice}
                        currency={currency}
                        size="lg"
                        className="text-store-accent font-bold text-xl"
                      />
                    </div>
                  )}

                  {/* Saved Designs */}
                  {isCustomizationEnabled && savedDesigns.length > 0 && (
                    <div className="space-y-2">
                      <Text
                        weight="semibold"
                        className="text-sm text-store-primary"
                      >
                        Your Saved Designs ({savedDesigns.length}):
                      </Text>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                        <p className="text-xs text-blue-800">
                          <strong>Ready to add to cart?</strong> Click the{" "}
                          <strong>"Add to Cart"</strong> button below to select
                          which designs you want and their quantities.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {savedDesigns.map((design: any) => (
                          <button
                            key={design._id}
                            type="button"
                            onClick={() => {
                              setCustomizationId(design._id);
                              setShowCustomizer(true);
                            }}
                            className="w-20 h-20 border-2 border-store-accent-light rounded-lg overflow-hidden hover:border-store-secondary hover:shadow-md transition-all relative group"
                            title={
                              design.name ||
                              `Design ${new Date(design.updatedAt || design.createdAt).toLocaleDateString()}`
                            }
                          >
                            {design.previewImage || design.modelPreviewImage ? (
                              <img
                                src={getImageUrl(
                                  product.customizationType === "2d-print"
                                    ? design.previewImage
                                    : design.previewImage ||
                                        design.modelPreviewImage,
                                  api.baseURL,
                                )}
                                alt="Design preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display =
                                    "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                <Text
                                  size="xs"
                                  color="muted"
                                  className="text-center px-1"
                                >
                                  {product.customizationType === "2d-print"
                                    ? "2D"
                                    : "3D"}
                                </Text>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <Text
                                  size="xs"
                                  className="text-white font-medium text-center px-1"
                                >
                                  Edit
                                </Text>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Customize Button - Compact */}
                  {isCustomizationEnabled && (
                    <div className="h-[40px]">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCustomizationId(null);
                          setShowCustomizer(true);
                        }}
                        className="w-full h-10 text-sm border-store-secondary text-store-primary hover:bg-store-background"
                      >
                        {savedDesigns.length > 0
                          ? "Start New Design"
                          : "Customize Product"}
                      </Button>
                    </div>
                  )}

                  {/* Add to Cart Button - Always visible */}
                  <div className="space-y-2">
                    {isCustomizationEnabled && savedDesigns.length > 0 && (
                      <div className="text-xs text-gray-600 text-center">
                        {savedDesigns.length} design
                        {savedDesigns.length !== 1 ? "s" : ""} ready to add to
                        cart
                      </div>
                    )}
                    <div className="h-[48px]">
                      <Button
                        ref={addToCartButtonRef}
                        onClick={handleAddToCart}
                        loading={isAddingToCart}
                        disabled={
                          (outOfStock && !product.isDigital) ||
                          isAddingToCart ||
                          (product.variantOptions &&
                          product.variantOptions.length > 0
                            ? !allVariantsSelected()
                            : !allRequiredSelected())
                        }
                        className={`w-full h-12 transition-all ${
                          (outOfStock && !product.isDigital) ||
                          (product.variantOptions &&
                          product.variantOptions.length > 0
                            ? !allVariantsSelected()
                            : !allRequiredSelected())
                            ? "bg-gray-400 cursor-not-allowed text-white"
                            : isCustomizationEnabled && savedDesigns.length > 0
                              ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                              : "bg-store-primary hover:bg-store-primary-dark text-white"
                        }`}
                      >
                        {outOfStock && !product.isDigital
                          ? t("productDetail.outOfStock")
                          : (
                                product.variantOptions &&
                                product.variantOptions.length > 0
                                  ? !allVariantsSelected()
                                  : !allRequiredSelected()
                              )
                            ? t("productDetail.selectVariants") ||
                              "Select Variants"
                            : product.isCustomizable && savedDesigns.length > 0
                              ? "Select Designs & Add to Cart"
                              : t("productDetail.addToCart")}
                      </Button>
                    </div>
                  </div>

                  {/* Description - Expandable */}
                  <div>
                    {displayDescription ? (
                      <div>
                        <div
                          className={`prose max-w-none text-store-primary-light text-base ${
                            !descriptionExpanded
                              ? "line-clamp-4 min-h-[96px]"
                              : ""
                          }`}
                          dangerouslySetInnerHTML={{
                            __html: displayDescription,
                          }}
                        />
                        {needsReadMore && (
                          <button
                            type="button"
                            onClick={() =>
                              setDescriptionExpanded(!descriptionExpanded)
                            }
                            className="mt-2 text-store-secondary hover:text-store-secondary-dark text-sm font-medium"
                          >
                            {descriptionExpanded ? "Read less" : "Read more"}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="min-h-[96px]"></div>
                    )}
                  </div>

                  {/* Product Info - Collapsible/Scrollable */}
                  {(product.vendor ||
                    product.tags?.length > 0 ||
                    product.metafields?.length > 0) && (
                    <div className="pt-3 max-h-[120px] overflow-y-auto">
                      {product.vendor && (
                        <Text size="xs" color="muted" className="mb-1">
                          <span className="font-semibold">Vendor:</span>{" "}
                          {product.vendor}
                        </Text>
                      )}
                      {product.tags && product.tags.length > 0 && (
                        <div className="mb-1">
                          <Text
                            size="xs"
                            color="muted"
                            className="font-semibold mb-0.5"
                          >
                            Tags:
                          </Text>
                          <div className="flex flex-wrap gap-1">
                            {product.tags.map((tag: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {product.metafields && product.metafields.length > 0 && (
                        <div>
                          <Text
                            size="xs"
                            color="muted"
                            className="font-semibold mb-0.5"
                          >
                            Details:
                          </Text>
                          <div className="space-y-0.5">
                            {product.metafields.map(
                              (metafield: any, idx: number) => (
                                <Text key={idx} size="xs" color="muted">
                                  <span className="font-semibold capitalize">
                                    {metafield.key.replace(/-/g, " ")}:
                                  </span>{" "}
                                  {metafield.value}
                                </Text>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Sidebar Zone */}
      <PageZone zoneName="sidebar" pageType="product-detail" />

      {/* Custom Footer Zone */}
      <PageZone zoneName="footer" pageType="product-detail" />

      {/* Image Zoom Pan Modal */}
      <ImageZoomPan
        src={currentImage.fullUrl}
        alt={currentImage.alt}
        isOpen={isZoomPanOpen}
        onClose={() => setIsZoomPanOpen(false)}
        images={productImages.map((img: any) => ({
          url: img.fullUrl,
          alt: img.alt,
          type: img.type,
        }))}
        currentIndex={selectedImageIndex}
        onIndexChange={(index) => {
          setSelectedImageIndex(index);
          handleImageClick(index);
        }}
      />
      {/* Design Selection Modal */}
      {product && isCustomizationEnabled && product.customizationType && (
        <DesignSelectionModal
          isOpen={showDesignSelectionModal}
          onClose={() => setShowDesignSelectionModal(false)}
          productId={product._id}
          customizationType={product.customizationType}
          onConfirm={handleDesignSelectionConfirm}
        />
      )}
    </div>
  );
};

export default ProductDetail;
